import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "The old way",
    body: "Legal help is billed by the hour — often $250 to $500 every time you pick up the phone. So most people simply don't. They sign contracts unread, ignore letters, and hope problems go away.",
    accent: false,
  },
  {
    n: "02",
    title: "The shift",
    body: "A legal membership flips the model. One flat monthly fee connects you to a dedicated provider law firm. Ask questions as often as you like — the meter isn't running.",
    accent: false,
  },
  {
    n: "03",
    title: "The outcome",
    body: "Advice when you need it, documents reviewed before you sign, letters and calls made on your behalf, and trial defense hours if things ever go that far. Small problems stay small.",
    accent: true,
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.8, ease: EASE },
  }),
};

export default function Manifesto() {
  return (
    <section className="bg-white py-24 md:py-36" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-6"
        >
          The honest pitch
        </motion.p>
        <motion.h2
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={1}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] max-w-3xl leading-[1.1]"
        >
          You deserve to know what you're buying <span className="text-stroke-blue">before</span> anyone asks for your email.
        </motion.h2>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={i + 1}
              className={`relative rounded-3xl p-8 md:p-10 border transition-transform duration-300 hover:-translate-y-1.5 ${
                c.accent
                  ? "bg-[#0B192C] border-[#0B192C] text-white"
                  : "bg-[#F8FAFC] border-slate-200"
              }`}
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <span className={`font-mono-accent text-5xl font-semibold ${c.accent ? "text-sky-400" : "text-blue-200"}`}>
                {c.n}
              </span>
              <h3 className={`font-display font-bold text-2xl mt-6 mb-4 tracking-tight ${c.accent ? "text-white" : "text-[#0B192C]"}`}>
                {c.title}
              </h3>
              <p className={`leading-relaxed ${c.accent ? "text-slate-300" : "text-slate-600"}`}>{c.body}</p>
            </motion.article>
          ))}
        </div>

        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={2}
          className="mt-16 rounded-3xl border border-blue-100 bg-[#EFF6FF] p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center"
          data-testid="cost-comparison"
        >
          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-blue-700 mb-3">The math, honestly</p>
            <p className="font-display font-bold text-2xl md:text-3xl text-[#0B192C] tracking-tight leading-snug">
              One hour with a traditional attorney can cost more than months of a membership.
            </p>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between text-sm font-medium text-slate-600 mb-2">
                <span>Traditional hourly attorney</span>
                <span className="font-mono-accent">$250–$500 / hr</span>
              </div>
              <div className="h-3 rounded-full bg-slate-200 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: EASE }}
                  className="h-full rounded-full bg-slate-400"
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm font-medium text-blue-900 mb-2">
                <span className="flex items-center gap-1.5">Legal membership <ArrowDownRight size={15} className="text-blue-600" /></span>
                <span className="font-mono-accent">from $39.95/mo</span>
              </div>
              <div className="h-3 rounded-full bg-blue-200 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "18%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.3, ease: EASE }}
                  className="h-full rounded-full bg-[#1D4ED8]"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">Illustrative comparison. Exact plan pricing and coverage shared on request.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
